<script setup lang="ts">
import { ref } from 'vue'
import { ActionType } from '@/modules/action/types/action.enums'
import type { UserClientAction } from '@/modules/user-management/models/user.models'
import type { UserSetting } from '../models/user.model'
import type {
  UserClientCarrierDto,
  UserClientServiceGroupDto,
  UserClientCountryDto,
  UserClientZipZoneDto,
  UserClientDimensionDto,
} from '@/api/models/types'

import DayRangeSlider from '@/shared/components/sliders/DayRangeSlider.vue'
import LineDivider from '@/shared/components/divider/LineDivider.vue'
import AutoComplete from '@/shared/components/auto-complete/AutoComplete.vue'
import CheckBox from '@/shared/components/check-box/CheckBox.vue'
import SegmentedControlButton from '@/shared/components/button/SegmentedControlButton.vue'
import TabContent from '@/shared/components/tab/TabContent.vue'

interface Props {
  userId?: number
  carriers: UserClientCarrierDto[]
  countries: UserClientCountryDto[]
  serviceGroups: UserClientServiceGroupDto[]
  zipZones: UserClientZipZoneDto[]
  dimensions: Record<number, UserClientDimensionDto[]>
  actions: UserClientAction[]
}

defineProps<Props>()

const model = defineModel<UserSetting>({ required: true })

const isSaving = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const tabIndex = ref<number>(0)

const action = ref<UserClientAction>()

const onActionChange = (): void => {
  if (!action.value) return

  model.value.outboundActionId = action.value.groupId
  model.value.outboundActionType = action.value.typeId
}

const onActionClear = (): void => {
  model.value.outboundActionId = 0
  model.value.outboundActionType = ActionType.None
}
</script>

<template>
  <SegmentedControlButton
    v-model="tabIndex"
    :items="[{ label: $t('glossary.outboundShipment', 2), disabled: isSaving }]"
    :loading="isLoading"
  />
  <TabContent v-model="tabIndex" :index="0">
    <div class="flex flex-col gap-2">
      <DayRangeSlider
        v-model:range="model.outboundViewDateRange"
        v-model:offset="model.outboundViewDateRangeOffset"
        :title="$t('glossary.viewDateRange')"
        :max="30"
        :min="-30"
        :max-span="60"
        :step="5"
        :percent="50"
      />
      <DayRangeSlider
        v-model:range="model.outboundActionDateRange"
        v-model:offset="model.outboundActionDateRangeOffset"
        :title="$t('glossary.actionDateRange')"
        :max="0"
        :min="-30"
        :max-span="30"
        :percent="100"
        :step="5"
        class="pb-4"
      />
      <LineDivider :title="$t('glossary.myShipment', 2)" />
      <div class="pt-2 w-32">
        <CheckBox
          v-model="model.outboundAssignedToMe"
          :title="$t('glossary.assignedToMe')"
          :label="true"
        />
      </div>
      <div class="pb-4">
        <AutoComplete
          v-if="actions.length > 0"
          v-model="action"
          item-text="name"
          :items="actions"
          :label="$t('glossary.action')"
          class="w-full"
          @change="onActionChange"
          @clear="onActionClear"
        />
      </div>
      <LineDivider :title="$t('glossary.location')" />
      <div class="flex gap-4 pb-4">
        <AutoComplete
          v-if="countries.length > 0"
          v-model="model.outboundCountryId"
          item-text="countryName"
          item-key="countryId"
          :items="countries"
          :label="$t('glossary.country')"
          :disabled="countries.length === 1"
          class="w-full"
        />
        <AutoComplete
          v-if="zipZones.length > 0"
          v-model="model.outboundZipZoneId"
          item-text="zoneName"
          item-key="zoneId"
          :items="zipZones"
          :label="$t('glossary.zipZone')"
          class="w-full"
        />
      </div>
      <LineDivider :title="$t('glossary.transportation')" />
      <div class="flex gap-4 pb-4">
        <AutoComplete
          v-if="carriers.length > 0"
          v-model="model.outboundCarrierId"
          item-text="carrierName"
          item-key="carrierId"
          :items="carriers"
          :label="$t('glossary.carrier')"
          :disabled="carriers.length === 1"
          class="w-full"
        />
        <AutoComplete
          v-if="serviceGroups.length > 0"
          v-model="model.outboundServiceGroupId"
          item-text="serviceGroupName"
          item-key="serviceGroupId"
          :items="serviceGroups"
          :label="$t('glossary.serviceGroup')"
          :disabled="serviceGroups.length === 1"
          class="w-full"
        />
      </div>
      <LineDivider :title="$t('glossary.dimension')" />
      <AutoComplete
        v-if="dimensions[1]"
        v-model="model.outboundDimension1Id"
        item-text="dimensionValueName"
        item-key="dimensionValueId"
        :items="dimensions[1]"
        :label="dimensions[1][0].dimensionName"
        :disabled="dimensions[1].length === 1"
        class="w-full"
      />
      <AutoComplete
        v-if="dimensions[2]"
        v-model="model.outboundDimension2Id"
        item-text="dimensionValueName"
        item-key="dimensionValueId"
        :items="dimensions[2]"
        :label="dimensions[2][0].dimensionName"
        :disabled="dimensions[2].length === 1"
        class="w-full"
      />
      <AutoComplete
        v-if="dimensions[3]"
        v-model="model.outboundDimension3Id"
        item-text="dimensionValueName"
        item-key="dimensionValueId"
        :items="dimensions[3]"
        :label="dimensions[3][0].dimensionName"
        :disabled="dimensions[3].length === 1"
        class="w-full"
      />
    </div>
  </TabContent>
</template>
